import Constants from "utils/Constants";
import { useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Button, Col, Row } from "reactstrap";
import DateTime from "../Inputs/DateTime/DateTime";
import DisplayRecord from "./DisplayRecord";
import useKeyEvent from "../../hooks/useKeyEvent";
import { Popover, Whisper } from "rsuite";
import AppSpinner from "../../elements/AppSpinner";
import { isoStringToFormat } from "../../utils/DateUtils";
import EmptyField from "./EmptyField";
import dayjs from "dayjs";

const DateTimeRecord = (
  {
    tableView,
    recordID,
    type,
    field,
    existingValue,
    modifyTrigger,
    inputSize,
    mode,
    onUpdate,
    submitting,
    error,
  },
  ref
) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(null);
  const [isDateTimeOpen, setIsDateTimeOpen] = useState(false); // Whether or the DateTime component is open

  const whisperRef = useRef();

  const startEditing = () => {
    let val = existingValue;

    if (
      val &&
      (field?.FieldType === Constants.FIELDTYPES.DATE ||
        field?.FieldType === Constants.FIELDTYPES.DATE_TIME)
    ) {
      val = dayjs(val);
    } else if (val && field?.FieldType === Constants.FIELDTYPES.TIME) {
      val = dayjs(val, "HH:mmZZ");
    }

    setIsEditing(true);
    setValue(val);
    whisperRef.current?.open();
  };

  const stopEditing = () => {
    setIsEditing(false);
    setValue(null);
    whisperRef.current?.close();
  };

  useKeyEvent(stopEditing);

  const save = (e) => {
    e.preventDefault();

    let val = null;

    if (
      value &&
      (field?.FieldType === Constants.FIELDTYPES.DATE ||
        field?.FieldType === Constants.FIELDTYPES.DATE_TIME)
    ) {
      val = value;
    } else if (value && field?.FieldType === Constants.FIELDTYPES.TIME) {
      val = dayjs(value);

      val = val.format("HH:mmZZ");
    }

    onUpdate({
      field,
      recordID,
      payload: { [field.APIName]: val },
      onSuccess: stopEditing,
    });
  };

  const handleChange = (value) => {
    setValue(value);
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef.current?.open();
    },
  }));

  return (
    <Whisper
      preventOverflow
      ref={whisperRef}
      placement="auto"
      trigger={isEditing && !isDateTimeOpen ? "click" : "none"}
      speaker={
        <Popover className="sidebar-popover p-0 m-0" style={{ width: "350px" }}>
          <div className="cursor-pointer p-2 text-dark border-bottom border-superlight">
            <Row className="align-items-center">
              <Col xs="">
                <h5 className="text-dark m-0">{field?.Name}</h5>
              </Col>
              <Col xs="auto">
                <Button
                  size="sm"
                  outline
                  color="secondary"
                  onClick={(e) => {
                    e.preventDefault();

                    stopEditing();
                  }}
                >
                  Done
                </Button>
              </Col>
            </Row>
          </div>
          <div className="p-2">
            <Row className="align-items-center">
              <Col xs="" className="pr-2">
                <DateTime
                  isRecord
                  type={type}
                  existingValue={existingValue}
                  field={field}
                  inputSize={inputSize}
                  onChange={handleChange}
                  setIsOpen={setIsDateTimeOpen}
                />
              </Col>
              <Col xs="auto" className="pl-0">
                <Button
                  disabled={(field?.Required && !value) || submitting}
                  size="sm"
                  color="primary"
                  className="btn-icon-only"
                  onClick={save}
                >
                  {submitting ? (
                    <AppSpinner color="white" />
                  ) : (
                    <i
                      className="mdi mdi-check"
                      style={{ fontSize: "20px" }}
                    ></i>
                  )}
                </Button>
              </Col>
            </Row>
          </div>
        </Popover>
      }
    >
      <div>
        <DisplayRecord
          mode={mode}
          tableView={tableView}
          modifyTrigger={modifyTrigger}
          openModify={startEditing}
          isSensitive={field?.Sensitive ?? false}
          displayComponent={(any, innerRef) => (
            <div ref={innerRef}>
              {field && existingValue ? (
                isoStringToFormat(existingValue, field.FieldType)
              ) : (
                <EmptyField table={tableView} />
              )}
            </div>
          )}
        />
      </div>
    </Whisper>
  );
};

export default forwardRef(DateTimeRecord);
